'use client';

import { bulkAssignCompliancesToUsers, useCompanyMembers } from '@commenda/api';
import { SingleCombobox } from '@commenda/kakashi';
import type { UnifiedFiling } from '@commenda/types/src/compliance-calendar/compliances/unified-filing';
import { useMemo, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';

/**
 * Tax & Reporting compliance-calendar table cells (rendered only on the `isTNAApp` variant).
 *
 * - `InlineAssigneeCell`: an inline assignee picker — selecting a member assigns this single
 *   filing to them (and clears any previous assignees), persisting via the bulk-assignees API.
 */

export const InlineAssigneeCell = ({
  filing,
  companyId,
  refreshCompliances,
}: {
  filing: UnifiedFiling;
  companyId?: number;
  refreshCompliances: () => void;
}) => {
  const { setNotification } = useNotification();
  const { data: membersData } = useCompanyMembers(companyId ?? 0);
  const [isSaving, setIsSaving] = useState(false);

  const options = useMemo(
    () =>
      (membersData?.members ?? []).map((member) => ({
        value: member.id,
        label: member.name,
      })),
    [membersData?.members]
  );

  const currentAssigneeIds = useMemo(
    () => filing.assignees.map((assignee) => assignee.id),
    [filing.assignees]
  );
  const selectedId = currentAssigneeIds[0] ?? '';

  const assign = async (nextId: string) => {
    if (!companyId || isSaving) return;
    setIsSaving(true);
    try {
      const res = await bulkAssignCompliancesToUsers({
        complianceItemIds: [filing.id],
        assigneeIdsToAdd: nextId ? [nextId] : undefined,
        assigneeIdsToRemove: currentAssigneeIds.filter((id) => id !== nextId),
        companyId,
      });
      if (res && !res.ok) throw new Error('assign failed');
      refreshCompliances();
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Could not update the assignee. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // Stop clicks on the picker from bubbling to the row's onClick (which opens the side panel).
    <div onClick={(e) => e.stopPropagation()} role="presentation">
      <SingleCombobox
        options={options}
        value={selectedId}
        setValue={(value) => assign(value || '')}
        placeholder="Unassigned"
        canClearSelection
        disabled={isSaving || !companyId}
      />
    </div>
  );
};
